import React, { useEffect, useState } from "react";
import PageLayout from "../components/PageLayout";
import Newsletter from "../components/Newsletter/Newsletter";
import { FaPlane } from "react-icons/fa";
import CustomButton from "../components/CustomButton/CustomButton";
import ImportantText from "../components/ImportantText/ImportantText";
import moment from "moment/moment";
import momentTz from 'moment-timezone';
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSearchResult } from "../api/getSearchResult";
import loader from "../images/loading.gif";

import "./search-results.scss";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { splitCapitalize } from "../utils/splitCapitalize";

const FLIGHT_STATUSES = {
  SCHEDULED: "Scheduled",
  ARRIVED: "Arrived",
  CANCELLED: "Canceled",
  DELAYED: "Delayed",
  DEPARTED: "Departed",
};

const MINUTES_IN_DAY = 1440;

const REDIRECTIONAL_LINKS = {
  REFUND: "https://flyflair.com/compensation-requests",
  HELP_DESK: "https://flyflair.zendesk.com/hc/en-us",
  MANAGE_BOOKING: "https://flyflair.com/my-trip",
  CHECK_IN: "https://oci.flyflair.com/",
};


const calculateTimeDifference = (scheduledDate, scheduledTime, estimatedDate, estimatedTime , dateDiff) => {

  const scheduled = moment(`${scheduledDate} ${scheduledTime}`, "YYYY-MM-DD HH:mm")

  const estimated = moment(`${estimatedDate} ${estimatedTime}`, "YYYY-MM-DD HH:mm")

  // UTC date issue wrong data from DB
  // if (dateDiff && dateDiff > 0) {
  //   estimated.subtract(dateDiff, 'days');
  // }

  let timeDifferenceMinutes
  if (estimated.isBefore(scheduled)) {
    timeDifferenceMinutes = scheduled.diff(estimated, "minutes");
  } else {
    timeDifferenceMinutes = estimated.diff(scheduled, "minutes");
  }

  return timeDifferenceMinutes;
  
}
  
const getLastUpdateDateTime = () =>{
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return momentTz().tz(timezone).format("ddd, MMM D YYYY hh:mm A zz")
}

const FlightResultTop = (flightSearchResults) => {
  const {
    arrival_city,
    arrival_station,
    flight_number,
    departure_city,
    departure_station,
    scheduled_date_of_aircraft_arrival_local,
    actual_date_of_aircraft_departure_local,
    scheduled_time_of_aircraft_departure_local,
    actual_time_of_aircraft_departure_local,
    scheduled_date_of_aircraft_departure_local
  } = flightSearchResults.flightSearchResults;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="flight-result-top">
      <div className="flight-result-top__header">
        <h1>{`${t("result-page.page-title")}`}</h1>
        <p>
          {`${t("result-page.route-title")}`}:{" "}
          <span>
            {splitCapitalize(departure_city)} to {splitCapitalize(arrival_city)}
          </span>
        </p>
        <p>
          {`${t("result-page.departure-date")}`}:{" "}
          <span>
            {moment(scheduled_date_of_aircraft_departure_local).format(
              "ddd, MMM D YYYY"
            )}{" "}
            {calculateTimeDifference(
              scheduled_date_of_aircraft_arrival_local,
              actual_date_of_aircraft_departure_local,
              scheduled_time_of_aircraft_departure_local,
              actual_time_of_aircraft_departure_local
            ) >= MINUTES_IN_DAY
              ? "+1"
              : ""}
          </span>
        </p>
        <div
          onClick={() =>
            navigate(
              `/?flightNumber=${flight_number.substring(
                2
              )}&tab=${searchParams.get(
                "tab"
              )}&arrival=${arrival_station}&departure=${departure_station}`
            )
          }
          className="modify display-mobile"
        >{`${t("result-page.modify")}`}</div>

        <div className="flight-result-top__header__bottom">
          <div>
            <p className="flight-result-top__header__bottom__last-update">
              {`${t("result-page.last-update")}`}
            </p>
            <p>{`${getLastUpdateDateTime()}`}</p>
          </div>

          <p
            onClick={() => navigate(0)}
            className="flight-result-top__header__bottom__refresh"
          >
            {`${t("result-page.refresh")}`}
          </p>
        </div>
      </div>
      <div
        onClick={() =>
          navigate(
            `/?flightNumber=${flight_number.substring(
              2
            )}&tab=${searchParams.get(
              "tab"
            )}&arrival=${arrival_station}&departure=${departure_station}`
          )
        }
        className="modify display-desktop"
      >{`${t("result-page.modify")}`}</div>
    </div>
  );
};

const DestinationBadge = ({ name, className = "" }) => (
  <div className={`destination-badge ${className}`}>
    <span>{name}</span>
  </div>
);

const ResultCard = ({ className, status, details }) => {  
  const { t } = useTranslation();

  const topSection = (status) => {
    // eslint-disable-next-line default-case
    switch (status) {
      case FLIGHT_STATUSES.DELAYED: {
        return (
          <>
            <div>
              {t('result-page.card.departure')}:{" "}
              <span className={`departure delayed`}>
                {t('result-page.card.delayed')}{" "}
                {calculateTimeDifference(
                  details?.scheduled_date_of_aircraft_departure_local,
                  details?.scheduled_time_of_aircraft_departure_local,
                  details?.estimated_date_of_aircraft_departure_local,
                  details?.estimated_time_of_aircraft_departure_local
                )}{" "}
                {t("result-page.card.mins")}
              </span>
            </div>
            <div>
              {t('result-page.card.arrival')}:{" "}
              <span className={`departure delayed`}>
                {t('result-page.card.delayed')}{" "}
                {calculateTimeDifference(
                  details?.scheduled_date_of_aircraft_arrival_local,
                  details?.scheduled_time_of_aircraft_arrival_local,
                  details?.estimated_date_of_aircraft_arrival_local,
                  details?.estimated_time_of_aircraft_arrival_local,
                  details?.date_variation
                )}{" "}
                {t("result-page.card.mins")}
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.ARRIVED: {
        const departureTime = calculateTimeDifference(
          details?.scheduled_date_of_aircraft_departure_local,
          details?.scheduled_time_of_aircraft_departure_local,
          details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local,
          details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local
        );

        const arrivalTime = calculateTimeDifference(
          details?.scheduled_date_of_aircraft_arrival_local,
          details?.scheduled_time_of_aircraft_arrival_local,
          details?.blockon_date_of_aircraft_arrival_local || details?.actual_date_of_aircraft_arrival_local,
          details?.blockon_time_of_aircraft_arrival_local || details?.actual_time_of_aircraft_arrival_local,
          details?.date_variation
        );

        const scheduledArrival = moment(`${details?.scheduled_date_of_aircraft_arrival_local} ${details?.scheduled_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")

        const estimatedArrival = moment(`${details?.blockon_date_of_aircraft_arrival_local || details?.actual_date_of_aircraft_arrival_local} ${details?.blockon_time_of_aircraft_arrival_local || details?.actual_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")

        return (
          <>
            <div>
              {`${t("result-page.card.departure")}`}:{" "}
              <span className={(() => {
                const scheduled = moment(`${details?.scheduled_date_of_aircraft_departure_local} ${details?.scheduled_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                const actual = moment(`${details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local} ${details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                return actual.isBefore(scheduled) ? 'departure on-time' : 'departure delayed';
              })()}>
                {(() => {
                  const scheduled = moment(`${details?.scheduled_date_of_aircraft_departure_local} ${details?.scheduled_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                  const actual = moment(`${details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local} ${details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                  
                  if (actual.isBefore(scheduled)) {
                    return `${departureTime} ${t("result-page.card.mins")} ${t('result-page.card.early').toLowerCase()}`;
                  } else if (departureTime > 0) {
                    return `${t('result-page.card.delayed-by')} ${departureTime} ${t("result-page.card.mins")}`;
                  } else {
                    return `${t('result-page.card.on-time')}`;
                  }
                })()}
              </span>
            </div>
            <div>
              {`${t("result-page.card.arrival")}`}:{" "}
              {scheduledArrival.isBefore(estimatedArrival) ? (
                <span className={`departure delayed`}>
                  {arrivalTime + ` ${t("result-page.card.mins")} ${t('result-page.card.late').toLowerCase()}`}
                </span>
              ) : (
                <span className={`departure on-time`}>{`${t('result-page.card.arrived')} ${
                  arrivalTime && Math.abs(arrivalTime) + ` ${t("result-page.card.mins")} ${t('result-page.card.early').toLowerCase()}`
                }`}</span>
              )}
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.DEPARTED: {
        const arrivalTime = calculateTimeDifference(
          details?.scheduled_date_of_aircraft_arrival_local,
          details?.scheduled_time_of_aircraft_arrival_local,
          details?.estimated_date_of_aircraft_arrival_local,
          details?.estimated_time_of_aircraft_arrival_local,
          details?.date_variation
        )

        const scheduledArrival = moment(`${details?.scheduled_date_of_aircraft_arrival_local} ${details?.scheduled_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")

        const estimatedArrival = moment(`${details?.estimated_date_of_aircraft_arrival_local} ${details?.estimated_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")

        return (
          <>
            <div>
              {`${t("result-page.card.departure")}`}:{" "}
              <span className={(() => {
                const scheduled = moment(`${details?.scheduled_date_of_aircraft_departure_local} ${details?.scheduled_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                const actual = moment(`${details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local} ${details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                return actual.isBefore(scheduled) ? 'departure on-time' : 'departure departed';
              })()}>
                {(() => {
                  const timeDiff = calculateTimeDifference(
                    details?.scheduled_date_of_aircraft_departure_local,
                    details?.scheduled_time_of_aircraft_departure_local,
                    details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local,
                    details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local
                  );
                  const scheduled = moment(`${details?.scheduled_date_of_aircraft_departure_local} ${details?.scheduled_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                  const actual = moment(`${details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local} ${details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                  
                  if (actual.isBefore(scheduled)) {
                    return `${t('result-page.card.departed')} - ${timeDiff} ${t("result-page.card.mins")} ${t('result-page.card.early').toLowerCase()}`;
                  } else {
                    return `${t('result-page.card.departed')} - ${timeDiff} ${t("result-page.card.mins")} ${t("result-page.card.delayed").toLowerCase()}`;
                  }
                })()}
              </span>
            </div>
            <div>
              <div>
                {`${t("result-page.card.arrival")}`}:{" "}
                {scheduledArrival.isBefore(estimatedArrival) ? (
                  <span className={`departure delayed`}>
                    {arrivalTime + ` ${t("result-page.card.mins")} ${t('result-page.card.late').toLowerCase()}`}
                  </span>
                ) : (
                  <span className={`departure on-time`}>{`${
                    arrivalTime &&  + Math.abs(arrivalTime) + ` ${t("result-page.card.mins")} ${t("result-page.card.early").toLowerCase()}`
                  }`}</span>
                )}
              </div>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.SCHEDULED: {
        return (
          <>
            <div>
              {`${t("result-page.card.departure")}`}:{" "}
              <span className={`departure on-time`}>
                {details?.actual_time_of_aircraft_departure_local
                  ? `${t("result-page.card.scheduled")} (` +
                    calculateTimeDifference(
                      details?.scheduled_date_of_aircraft_departure_local,
                      details?.scheduled_time_of_aircraft_departure_local,
                      details?.estimated_date_of_aircraft_departure_local,
                      details?.estimated_time_of_aircraft_departure_local
                    ) +
                    `) ${t("result-page.card.mins")}`
                  : `${t('result-page.card.on-time')}`}
              </span>
            </div>
            <div>
              {`${t("result-page.card.arrival")}`}:{" "}
              <span className={`departure on-time`}>
                {details?.actual_time_of_aircraft_arrival_local
                  ? `${t("result-page.card.scheduled")} (` +
                    calculateTimeDifference(
                      details?.scheduled_date_of_aircraft_arrival_local,
                      details?.scheduled_time_of_aircraft_arrival_local,
                      details?.estimated_date_of_aircraft_arrival_local,
                      details?.estimated_time_of_aircraft_arrival_local,
                      details?.date_variation
                    ) +
                    `) ${t("result-page.card.mins")}`
                  : `${t('result-page.card.on-time')}`}
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.CANCELLED: {
        return (
          <DestinationBadge name="Cancelled" className="cancelled-badge" />
        );
      }
    }
  };

  const mobileTopSectionDeparture = (status) => {
    // eslint-disable-next-line default-case
    switch (status) {
      case FLIGHT_STATUSES.DELAYED: {
        return (
          <>
            <div>
            {t('result-page.card.departure')}:{" "}
              <span className={`departure delayed`}>
                {t('result-page.card.delayed')} (
                {calculateTimeDifference(
                  details?.scheduled_date_of_aircraft_departure_local,
                  details?.scheduled_time_of_aircraft_departure_local,
                  details?.estimated_date_of_aircraft_departure_local,
                  details?.estimated_time_of_aircraft_departure_local
                )}{" "}
                {t("result-page.card.mins")})
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.ARRIVED: {
        const departureTime = calculateTimeDifference(
          details?.scheduled_date_of_aircraft_departure_local,
          details?.scheduled_time_of_aircraft_departure_local,
          details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local,
          details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local
        );

        return (
          <>
            <div>
              {`${t("result-page.card.departure")}`}:{" "}
              <span className={`departure delayed`}>
                {details?.actual_time_of_aircraft_departure_local &&
                departureTime > 0
                  ? `${t('result-page.card.delayed-by')} ` + departureTime + ` ${t("result-page.card.mins")}`
                  : `${t('result-page.card.on-time')}`}
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.DEPARTED: {
        return (
          <>
            <div>
              {t("result-page.card.departure")}:{" "}
              <span className={(() => {
                const scheduled = moment(`${details?.scheduled_date_of_aircraft_departure_local} ${details?.scheduled_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                const actual = moment(`${details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local} ${details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                return actual.isBefore(scheduled) ? 'departure on-time' : 'departure departed';
              })()}>
                {(() => {
                  const timeDiff = calculateTimeDifference(
                    details?.scheduled_date_of_aircraft_departure_local,
                    details?.scheduled_time_of_aircraft_departure_local,
                    details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local,
                    details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local
                  );
                  const scheduled = moment(`${details?.scheduled_date_of_aircraft_departure_local} ${details?.scheduled_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                  const actual = moment(`${details?.blockoff_date_of_aircraft_departure_local || details?.actual_date_of_aircraft_departure_local} ${details?.blockoff_time_of_aircraft_departure_local || details?.actual_time_of_aircraft_departure_local}`, "YYYY-MM-DD HH:mm");
                  
                  if (actual.isBefore(scheduled)) {
                    return `${t('result-page.card.departed')} - ${timeDiff} ${t("result-page.card.mins")} ${t('result-page.card.early').toLowerCase()}`;
                  } else {
                    return `${t('result-page.card.departed')} - ${timeDiff} ${t("result-page.card.mins")} ${t("result-page.card.delayed").toLowerCase()}`;
                  }
                })()}
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.SCHEDULED: {
        return (
          <>
            <div>
              {`${t("result-page.card.departure")}`}:{" "}
              <span className={`departure on-time`}>
                {details?.actual_time_of_aircraft_departure_local
                  ? `${t("result-page.card.scheduled")} (` +
                    calculateTimeDifference(
                      details?.scheduled_date_of_aircraft_departure_local,
                      details?.scheduled_time_of_aircraft_departure_local,
                      details?.estimated_date_of_aircraft_departure_local,
                      details?.estimated_time_of_aircraft_departure_local
                    ) +
                    `) ${t("result-page.card.mins")}`
                  : `${t('result-page.card.on-time')}`}
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.CANCELLED: {
        return (
          <DestinationBadge name="Cancelled" className="cancelled-badge" />
        );
      }
    }
  };

  const mobileTopSectionArrival = (status) => {
    // eslint-disable-next-line default-case
    switch (status) {
      case FLIGHT_STATUSES.DELAYED: {
        return (
          <>
            <div>
              {t('result-page.card.arrival')}:{" "}
              <span className={`departure delayed`}>
                {t('result-page.card.delayed')} (
                {calculateTimeDifference(
                  details?.scheduled_date_of_aircraft_arrival_local,
                  details?.scheduled_time_of_aircraft_arrival_local,
                  details?.estimated_date_of_aircraft_arrival_local,
                  details?.estimated_time_of_aircraft_arrival_local
                )}{" "}
                {t("result-page.card.mins")})
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.ARRIVED: {
        const arrivalTime = calculateTimeDifference(
          details?.scheduled_date_of_aircraft_arrival_local,
          details?.scheduled_time_of_aircraft_arrival_local,
          details?.actual_date_of_aircraft_arrival_local,
          details?.actual_time_of_aircraft_arrival_local,
          details?.date_variation
        );

        const scheduledArrival = moment(`${details?.scheduled_date_of_aircraft_arrival_local} ${details?.scheduled_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")

        const estimatedArrival = moment(`${details?.actual_date_of_aircraft_arrival_local} ${details?.actual_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")
        return (
          <>
            <div>
              {`${t("result-page.card.arrival")}`}:{" "}
              {scheduledArrival.isBefore(estimatedArrival) ? (
                <span className={`departure delayed`}>
                  {arrivalTime + ` ${t("result-page.card.mins")} ${t('result-page.card.late').toLowerCase()}`}
                </span>
              ) : (
                <span className={`departure on-time`}>{`${t('result-page.card.arrived')} ${
                   arrivalTime && Math.abs(arrivalTime) + ` ${t("result-page.card.mins")} ${t("result-page.card.early").toLowerCase()}`
                }`}</span>
              )}
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.DEPARTED: {
        
        const arrivalTime = calculateTimeDifference(
          details?.scheduled_date_of_aircraft_arrival_local,
          details?.scheduled_time_of_aircraft_arrival_local,
          details?.estimated_date_of_aircraft_arrival_local,
          details?.estimated_time_of_aircraft_arrival_local,
          details?.date_variation
        )

        const scheduledArrival = moment(`${details?.scheduled_date_of_aircraft_arrival_local} ${details?.scheduled_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")

        const estimatedArrival = moment(`${details?.estimated_date_of_aircraft_arrival_local} ${details?.estimated_time_of_aircraft_arrival_local}`, "YYYY-MM-DD HH:mm")
        return (
          <>
            <div>
                {`${t("result-page.card.arrival")}`}:{" "}
                {scheduledArrival.isBefore(estimatedArrival) ? (
                  <span className={`departure delayed`}>
                    {arrivalTime + ` ${t("result-page.card.mins")} ${t('result-page.card.late').toLowerCase()}`}
                  </span>
                ) : (
                  <span className={`departure on-time`}>{`${t('result-page.card.arrived')} ${
                   arrivalTime && Math.abs(arrivalTime) + ` ${t("result-page.card.mins")} ${t("result-page.card.early").toLowerCase()}`
                }`}</span>
                )}
              </div>
          </>
        );
      }

      case FLIGHT_STATUSES.SCHEDULED: {
        return (
          <>
            <div>
              {`${t("result-page.card.arrival")}`}:{" "}
              <span className={`departure on-time`}>
                {details?.actual_time_of_aircraft_arrival_local
                  ?`${t("result-page.card.scheduled")} (` +
                    calculateTimeDifference(
                      details?.scheduled_date_of_aircraft_arrival_local,
                      details?.scheduled_time_of_aircraft_arrival_local,
                      details?.actual_date_of_aircraft_arrival_local,
                      details?.actual_time_of_aircraft_arrival_local
                    ) +
                    `) ${t("result-page.card.mins")}`
                  : `${t('result-page.card.on-time')}`}
              </span>
            </div>
          </>
        );
      }

      case FLIGHT_STATUSES.CANCELLED: {
        return (
          <DestinationBadge name="Cancelled" className="cancelled-badge" />
        );
      }
    }
  };

  const flightArrivalTime =
    details?.blockon_time_of_aircraft_arrival_local ||
    details?.actual_time_of_aircraft_arrival_local ||
    details?.estimated_time_of_aircraft_arrival_local ||
    details?.scheduled_time_of_aircraft_arrival_local;

  const flightDepartureTime =
    details?.blockoff_time_of_aircraft_departure_local ||
    details?.actual_time_of_aircraft_departure_local ||
    details?.estimated_time_of_aircraft_departure_local ||
    details?.scheduled_time_of_aircraft_departure_local;

  return (
    <>
      <div className={`result-card ${className} show-desktop`}>
        <div className="result-card__top">
          {topSection(details?.flight_status)}
        </div>
        <div className="flight-icon">
          <FaPlane /> {details?.flight_number}
        </div>
        <div className="result-card__bottom">
          <div className="result-card__bottom__left">
            <div className="point">
              {splitCapitalize(details?.departure_city)}
            </div>
            <DestinationBadge name={details?.departure_station} />

            <div className="actual">
              {[FLIGHT_STATUSES.CANCELLED].includes(details?.flight_status)
                ? ""
                : `${t(
                    "result-page.card.actual-time"
                  )}: ${flightDepartureTime} ${
                    details?.departure_timezone_abbreviation
                  }`}
            </div>
            <div
              className={`${t('result-page.card.scheduled')} ${
                ["delayed", "cancelled"].includes(status) && "strike-through"
              }`}
            >
              {`${t("result-page.card.scheduled-time")}: ${
                details?.scheduled_time_of_aircraft_departure_local
              } ${details?.departure_timezone_abbreviation}`}
            </div>
          </div>
          <div className="result-card__bottom__center">
            <div className="flight-icon__center">
              <FaPlane /> {details?.flight_number}
            </div>
          </div>
          <div className="result-card__bottom__right">
            <div className="point">
              {splitCapitalize(details?.arrival_city)}
            </div>
            <DestinationBadge name={details?.arrival_station} />

            <div className="actual">
              {["cancelled"].includes(status)
                ? ""
                : `${t("result-page.card.actual-time")}: ${flightArrivalTime} ${
                    details?.arrival_timezone_abbreviation
                  }`}
            </div>
            <div
              className={`${t('result-page.card.scheduled')} ${
                ["delayed", "cancelled"].includes(status) && "strike-through"
              }`}
            >
              {`${t("result-page.card.scheduled-time")}: ${
                details?.scheduled_time_of_aircraft_arrival_local
              } ${details?.arrival_timezone_abbreviation}`}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile card */}
      <div className={`mobile-result-card ${className} show-mobile`}>
        <div className="departure-status">
          {mobileTopSectionDeparture(details?.flight_status)}
        </div>
        <div className="flight">
          <FaPlane />
          {details?.flight_number}
        </div>
        <div className="card-details top">
          <div className="card-left">
            <div className="from-city">
              {splitCapitalize(details?.departure_city)}
            </div>
            <div>
              <DestinationBadge name={details?.departure_station} />
            </div>
          </div>
          <div className="time">
            <div className="actual">
              {[FLIGHT_STATUSES.CANCELLED].includes(details?.flight_status)
                ? ""
                : `${t(
                    "result-page.card.actual-time"
                  )}: ${flightDepartureTime} ${
                    details?.departure_timezone_abbreviation
                  }`}
            </div>
            <div
              className={`${t('result-page.card.scheduled')} ${
                ["delayed", "cancelled"].includes(status) && "strike-through"
              }`}
            >
              {`${t("result-page.card.scheduled-time")}: ${
                details?.scheduled_time_of_aircraft_departure_local
              } ${details?.departure_timezone_abbreviation}`}
            </div>
          </div>
        </div>

        <hr className="divider" />

        <div className="departure-status">
          {mobileTopSectionArrival(details?.flight_status)}
        </div>
        <div className="card-details bottom">
          <div className="card-left">
            <div className="to-city">
              {splitCapitalize(details?.arrival_city)}
            </div>
            <div>
              <DestinationBadge name={details?.arrival_station} />
            </div>
          </div>
          <div className="time">
            <div className="actual">
              {["cancelled"].includes(status)
                ? ""
                : `${t("result-page.card.actual-time")}: ${flightArrivalTime} ${
                    details?.arrival_timezone_abbreviation
                  }`}
            </div>
            <div
              className={`${t('result-page.card.scheduled')} ${
                ["delayed", "cancelled"].includes(status) && "strike-through"
              }`}
            >
              {`${t("result-page.card.scheduled-time")}: ${
                details?.scheduled_time_of_aircraft_arrival_local
              } ${details?.arrival_timezone_abbreviation}`}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const OnTimeResultCard = ({ details }) => (
  <ResultCard
    className="result-card__on-time"
    status="on-time"
    details={details}
  />
);

const DepartedResultCard = ({ details }) => (
  <ResultCard
    className="result-card__delayed"
    status="delayed"
    details={details}
  />
);

const CancelledResultCard = ({ details }) => (
  <ResultCard
    details={details}
    className="result-card__cancelled"
    status="cancelled"
  />
);

const RefundContent = () => {
  const { t } = useTranslation();

  return (
    <div className="refund-content">
      <div className="refund-content__heading">
        {`${t("result-page.flight-cancelled-header")}`}
      </div>
      <div className="refund-content__description">
        {`${t("result-page.flight-cancelled-body")}`}
      </div>
      <div className="refund-content__description">
        {`${t("result-page.flight-cancelled-bottom")}`}
      </div>
      <div className="refund-content__bottom">
        <a href={REDIRECTIONAL_LINKS.REFUND} target="_blank" rel="noreferrer">
          <CustomButton
            name={`${t("result-page.refund")}`}
            classnames="primary"
          />
        </a>
        <div>
          <a
            href={REDIRECTIONAL_LINKS.HELP_DESK}
            target="_blank"
            rel="noreferrer"
            className="help-center"
          >
            {`${t("result-page.help-centre")}`}
          </a>
        </div>
      </div>
    </div>
  );
};

const ForgotAnythingSection = () => {
  const { t } = useTranslation();

  return (
    <div className="refund-content">
      <div className="refund-content__heading">{`${t(
        "result-page.forget-anything"
      )}`}</div>
      <div className="refund-content__description">
        {t("result-page.manage-booking-text-1")}{" "}
        <span>{t("result-page.manage-booking-text-2")}:</span>
      </div>
      <div className="refund-content__bottom">
        <a href={REDIRECTIONAL_LINKS.CHECK_IN} target="_blank" rel="noreferrer">
          <CustomButton
            name={`${t("result-page.check-in")}`}
            classnames="primary w-137"
          />
        </a>
      </div>

      <div className="refund-content__description bottom">
        {t("result-page.manage-booking-text-3")}
        <span>{t("result-page.manage-booking-text-4")}:</span>
      </div>
      <div className="refund-content__bottom">
        <a
          href={REDIRECTIONAL_LINKS.MANAGE_BOOKING}
          target="_blank"
          rel="noreferrer"
        >
          <CustomButton
            name={`${t("result-page.manage-booking")}`}
            classnames="ghost w-164"
          />
        </a>
      </div>
    </div>
  );
};

const IconButton = ({ icon, text, onClick, size, className }) => {
  return (
    <button className={`icon-button ${className}`} onClick={onClick}>
      <FontAwesomeIcon icon={icon} size={size} />
      <span>{text}</span>
    </button>
  );
};

// Main Component
function SearchResults() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [flightData, setFlightData] = useState(null);
  const [cancelledExist, setCancelledExist] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const renderResultCard = (status, flightData) => {
    switch (status) {
      case FLIGHT_STATUSES.SCHEDULED:
        return (
          <>
            <OnTimeResultCard details={flightData} />
          </>
        );
      case FLIGHT_STATUSES.ARRIVED:
        return (
          <>
            {moment(flightData?.scheduled_time_of_aircraft_arrival_local, "HH:mm").isAfter(moment(flightData?.actual_time_of_aircraft_arrival_local, "HH:mm")) ? (
              <OnTimeResultCard details={flightData} />
            ) : (
              <DepartedResultCard details={flightData} />
            )}
          </>
        );
      case FLIGHT_STATUSES.DEPARTED:
        // this condition is added as there the departure time is not always updated in the DB
        return (
          <>
            {moment(flightData?.scheduled_time_of_aircraft_arrival_local, "HH:mm").isAfter(moment(flightData?.actual_time_of_aircraft_arrival_local, "HH:mm")) ? (
              <OnTimeResultCard details={flightData} />
            ) : (
              <DepartedResultCard details={flightData} />
            )}
          </>
        );
        case FLIGHT_STATUSES.DELAYED:
        return (
          <>
            <DepartedResultCard details={flightData} />
          </>
        );
      case FLIGHT_STATUSES.CANCELLED:
        return (
          <>
            <CancelledResultCard details={flightData} />
          </>
        );
      default:
        <div className="search-results__main">
          <IconButton
              icon={faArrowLeft}
              text={`${t("result-page.back")}`}
              size="lg"
              onClick={() => navigate("/")}
            />
          <h1>No Flight found</h1>;
        </div>
    }
  };

  const fetchResults = async () => {
    setLoading(true);

    try {
      const response = await getSearchResult({
        flightNumber: searchParams.get("flightNumber"),
        departureDate: searchParams.get("flightDate"),
        departureStation: searchParams.get("departureStation"),
        arrivalStation: searchParams.get("arrivalStation"),
      });

      response.data.map((flight) => {
        if ([FLIGHT_STATUSES.CANCELLED].includes(flight.flight_status)) {
          setCancelledExist(true);
        }
      });

      if(response.data.length > 1) {
        // keep track of cancelled flight duplicates
        let seenCancelled = new Set()

        // filter through cancel status flights, and filter out duplicate flight numbers
        const filteredFlights = response.data.filter((flight) => {
          if (flight.flight_status === FLIGHT_STATUSES.CANCELLED) {
            if (seenCancelled.has(flight.flight_number)) {
              return false
            }
            seenCancelled.add(flight.flight_number)
          }
          return true
        });

        setFlightData(filteredFlights)
      } else {
        setFlightData(response.data)
      }

    } catch (err) {
      setFlightData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchResults();
  }, []);

  const sortedFlightData = flightData
    ? [...flightData].sort((a, b) => {
        const dateA = `${a.scheduled_date_of_aircraft_departure_local} ${a.scheduled_time_of_aircraft_departure_local}`;
        const dateB = `${b.scheduled_date_of_aircraft_departure_local} ${b.scheduled_time_of_aircraft_departure_local}`;
        return new Date(dateA) - new Date(dateB);
      })
    : [];

  return (
    <PageLayout>
      <div className="hero-image show-desktop">
        <img alt="flair airlines" src="images/flair-airlines.jpg" />
      </div>
      <div className="search-result">
        {loading ? (
          <div className="search-results__main loading-screen">
            <img src={loader} alt="loading" className="page-loader" />
          </div>
        ) : flightData ? (
          <div className="search-results__main">
            <IconButton
              icon={faArrowLeft}
              text={`${t("result-page.back")}`}
              size="lg"
              onClick={() => navigate("/")}
            />
            <FlightResultTop flightSearchResults={sortedFlightData[0]} />
            {sortedFlightData.map((flight) =>
              renderResultCard(flight.flight_status, flight)
            )}
            {cancelledExist ? <RefundContent /> : <ForgotAnythingSection />}
          </div>
        ) : (
          <div className="search-results__main">
            <IconButton
              className="back-button-padding"
              icon={faArrowLeft}
              text={`${t("result-page.back")}`}
              size="lg"
              onClick={() => navigate("/")}
            />
            <h1>No Flight Found</h1>
          </div>
        )}
        <ImportantText />
        <Newsletter />
      </div>
    </PageLayout>
  );
}

export default SearchResults;
